/*
 * Pure helpers behind FpuiInputCheckboxTree.
 *
 * A tree value is the list of checked ids. A parent id stands for the parent and all
 * its children, loaded or not: a parent whose children are still to load can be
 * checked as a whole. A node is `checked`, `partial` (some children checked) or
 * `unchecked`.
 */

export const CHECKED = 'checked'
export const PARTIAL = 'partial'
export const UNCHECKED = 'unchecked'

function hasChildren (node) {
  return Array.isArray(node.children) && node.children.length > 0
}

// A node can be opened when it has children, or when they are still to load.
export function isExpandable (node) {
  return Array.isArray(node.children) ? node.children.length > 0 : !!node.lazy
}

function stateFromChildren (children, states) {
  const list = children.map(child => states.get(child.id))
  if (list.every(state => state === CHECKED)) return CHECKED
  return list.some(state => state !== UNCHECKED) ? PARTIAL : UNCHECKED
}

/** Returns a Map of node id to its state, for every loaded node. */
export function computeStates (nodes, value, inherited = false, states = new Map()) {
  const checked = value instanceof Set ? value : new Set(value)
  nodes.forEach(node => {
    const own = inherited || checked.has(node.id)
    if (hasChildren(node)) computeStates(node.children, checked, own, states)
    states.set(node.id, !own && hasChildren(node) ? stateFromChildren(node.children, states) : (own ? CHECKED : UNCHECKED))
  })
  return states
}

/** State of the whole tree, for an "all" box above it. */
export function rootState (nodes, value) {
  if (!nodes.length) return UNCHECKED
  return stateFromChildren(nodes, computeStates(nodes, value))
}

// Turns states back into the shortest value: a fully checked parent is listed alone.
function collect (nodes, states, out = []) {
  nodes.forEach(node => {
    const state = states.get(node.id)
    if (state === CHECKED) out.push(node.id)
    else if (state === PARTIAL) collect(node.children, states, out)
  })
  return out
}

function findPath (nodes, id, path = []) {
  for (const node of nodes) {
    if (node.id === id) return [...path, node]
    if (hasChildren(node)) {
      const found = findPath(node.children, id, [...path, node])
      if (found) return found
    }
  }
  return null
}

/**
 * Checks the node and everything under it, or unchecks it when it is fully checked
 * (a partial node gets checked). Returns the new value.
 */
export function toggle (nodes, value, id) {
  const path = findPath(nodes, id)
  if (!path) return value
  const states = computeStates(nodes, value)
  const node = path[path.length - 1]
  const next = states.get(id) === CHECKED ? UNCHECKED : CHECKED
  const mark = n => {
    states.set(n.id, next)
    if (hasChildren(n)) n.children.forEach(mark)
  }
  mark(node)
  for (let i = path.length - 2; i >= 0; i--) {
    states.set(path[i].id, stateFromChildren(path[i].children, states))
  }
  return collect(nodes, states)
}

/** Checks every node, or none when all of them are already checked. */
export function toggleAll (nodes, value) {
  return rootState(nodes, value) === CHECKED ? [] : nodes.map(node => node.id)
}

/** The rows to draw: each node with its depth, children only under an opened node. */
export function visibleRows (nodes, expanded, depth = 0, rows = []) {
  nodes.forEach(node => {
    rows.push({ node, depth })
    if (expanded.has(node.id) && hasChildren(node)) visibleRows(node.children, expanded, depth + 1, rows)
  })
  return rows
}
